import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from '../lib/utils.js';
import { Button } from './Button.js';
import { DatePopover } from './DatePopover.js';
import { DateTrigger } from './DateTrigger.js';
import { DayInput } from './DayInput.js';
import { CalendarMonth, monthCursorFor, sameMonth } from './CalendarMonth.js';
import { useDateFieldLabels, type DateFieldLabels } from './dateLabels.js';
import { addMonths, clampDayKey, isDayKey, withinBounds, type DayKey } from '../lib/dayKey.js';
import { formatDayFirst } from '../lib/dayKeyText.js';

/* ===========================================================================
 * ONE DATE — the replacement for `<input type="date">`.
 *
 * THE VALUE IS A DAY KEY. `value` and `onChange` speak `YYYY-MM-DD` strings and
 * nothing else: no `Date`, no instant, no epoch. docs/time-and-dates.md is
 * binding and `pnpm run check:datebasis` gates it — the company timezone is the
 * system clock and the browser's is a viewing preference, so a control that
 * handed a `Date` back would leave every caller to truncate it in whichever
 * zone their machine happens to sit in.
 *
 * AND "TODAY" IS A PROP. There is no default and no fallback to a live clock.
 * apps/web has `useCompanyToday()`, which is the tenant's day; this component
 * cannot ask that question and so does not try. Given nothing, it draws no
 * today-ring and its Today button does not render — an honest absence rather
 * than a confident wrong day (design-rules §3.3).
 * ======================================================================== */

export interface DateFieldProps {
  /** `YYYY-MM-DD`, or '' for nothing chosen. */
  value: string;
  /** The chosen day, as a day key. Never a Date. */
  onChange: (value: DayKey) => void;
  /** The company's today, as a day key — `useCompanyToday()` in apps/web. */
  today?: string | null;
  min?: string | null;
  max?: string | null;
  disabled?: boolean;
  /** Announced on the trigger as `aria-required`. See `DateTrigger`. */
  required?: boolean;
  /** Marks the trigger invalid; the caller owns the message. */
  invalid?: boolean;
  /** Fires when the reader empties the field. Defaults to `onChange('')`. */
  onClear?: () => void;
  /**
   * How the CHOSEN day reads on the trigger. Defaults to the day-first key
   * (`12-10-2026`), which is what a field asking "which day" should say.
   *
   * A field whose day is a CONSEQUENCE rather than a coordinate wants the
   * consequence in the control's own words instead — an announcement's end
   * date reads "Ends 12 Oct", so the form does not need a sentence under the
   * row explaining what the date does (§3.12, stated by the control rather
   * than beside it). Only the chosen-day text; the placeholder, the popover
   * and everything a screen reader hears are untouched.
   */
  formatValue?: (value: string) => string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  /** Per-field overrides on top of the app's `DateFieldLabelsProvider`. */
  labels?: Partial<DateFieldLabels>;
  className?: string;
}

export function DateField({
  value,
  onChange,
  today,
  min,
  max,
  disabled,
      required,
  invalid,
  onClear,
  formatValue,
  id,
  labels: labelOverride,
  className,
  ...aria
}: DateFieldProps): JSX.Element {
  const labels = useDateFieldLabels(labelOverride);
  const panelId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /* THE CARET GOES BACK, AND IT GOES BACK AFTER THE PANEL HAS GONE. Focusing
     the trigger while the focus trap is still mounted makes the trap pull focus
     straight back into a panel that is about to unmount, and the reader lands
     on `<body>` with no way back to the control they just used. Same ref-and-
     effect round trip `FiltersPopover` uses, for the same reason. */
  const restoreFocus = useRef(false);
  const [open, setOpen] = useState(false);
  const [focusActive, setFocusActive] = useState(false);

  /* WHERE THE GRID OPENS, in priority order: the chosen day, else today, else
     the nearest legal day. Never a live clock, and never January of year zero —
     a picker that opens somewhere unrelated to the value it holds makes the
     reader re-navigate to what they already had. */
  const seed = isDayKey(value) ? value : isDayKey(today) ? (today as DayKey) : min || max || '';
  const [cursor, setCursor] = useState<DayKey>(() =>
    seed ? monthCursorFor(seed) : monthCursorFor('2000-01-01')
  );
  const [focusedKey, setFocusedKey] = useState<DayKey>(() =>
    seed ? clampDayKey(seed, min, max) : '2000-01-01'
  );

  const dismiss = useCallback(() => {
    restoreFocus.current = true;
    setOpen(false);
    setFocusActive(false);
  }, []);

  useEffect(() => {
    if (open || !restoreFocus.current) return;
    restoreFocus.current = false;
    triggerRef.current?.focus();
  }, [open]);

  function openPanel(): void {
    const start = isDayKey(value) ? value : isDayKey(today) ? (today as DayKey) : focusedKey;
    const landing = clampDayKey(start, min, max);
    setFocusedKey(landing);
    setCursor(monthCursorFor(landing));
    setFocusActive(false);
    setOpen(true);
  }

  function moveCaret(next: DayKey): void {
    setFocusedKey(next);
    // The caret leads the month: arrowing off the end of September shows
    // October rather than leaving the reader focused on a cell they cannot see.
    if (!sameMonth(next, cursor)) setCursor(monthCursorFor(next));
    setFocusActive(true);
  }

  function choose(key: DayKey): void {
    if (!withinBounds(key, min, max)) return;
    onChange(key);
    dismiss();
  }

  return (
    <div ref={anchorRef} className={cn('relative', className)}>
      <DateTrigger
        required={required}
        ref={triggerRef}
        id={id}
        text={formatValue ? formatValue(value) : formatDayFirst(value)}
        placeholder={labels.placeholder}
        open={open}
        panelId={panelId}
        disabled={disabled}
        invalid={invalid}
        glyph="calendar"
        ariaLabel={aria['aria-label'] ?? (aria['aria-labelledby'] ? undefined : labels.triggerAria)}
        ariaLabelledBy={aria['aria-labelledby']}
        ariaDescribedBy={aria['aria-describedby']}
        onToggle={() => (open ? dismiss() : openPanel())}
      />

      <DatePopover
        open={open}
        onDismiss={dismiss}
        triggerRef={triggerRef}
        anchorRef={anchorRef}
        id={panelId}
        ariaLabel={aria['aria-label'] ?? labels.triggerAria}
      >
        <div className="flex flex-col gap-component">
          <DayInput
            value={value}
            labels={labels}
            label={labels.placeholder}
            min={min}
            max={max}
            autoFocus
            onCommit={(key) => {
              onChange(key);
              setFocusedKey(key);
              setCursor(monthCursorFor(key));
            }}
            onClear={() => (onClear ? onClear() : onChange(''))}
          />

          {/* ANNOUNCED, NOT DRAWN. A month change is the one thing in this
              popover a sighted reader gets for free — the heading redraws — and
              a screen-reader user gets nothing at all, because the grid's own
              label changing is not an event. */}
          <p aria-live="polite" className="sr-only">
            {`${labels.monthNames[Number(cursor.slice(5, 7)) - 1] ?? ''} ${cursor.slice(0, 4)}`}
          </p>

          <CalendarMonth
            cursor={cursor}
            today={today ?? null}
            selectedStart={isDayKey(value) ? value : null}
            selectedEnd={isDayKey(value) ? value : null}
            min={min}
            max={max}
            focusedKey={focusedKey}
            onFocusedKeyChange={moveCaret}
            onSelect={choose}
            ownsFocus
            focusActive={focusActive}
            onEscape={dismiss}
            labels={labels}
            onPrevMonth={() => setCursor(addMonths(cursor, -1))}
            onNextMonth={() => setCursor(addMonths(cursor, 1))}
          />

          {/* GHOST BUTTONS, both of them. Today is a shortcut and Clear is a way
              back; neither is the answer the popover is open to get, so neither
              may carry a fill (the weight rule `Dialog.tsx` states for footers).
              Today renders only when the caller supplied one — see the header. */}
          <div className="flex items-center justify-between gap-element">
            {isDayKey(today) ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!withinBounds(today as DayKey, min, max)}
                onClick={() => choose(today as DayKey)}
              >
                {labels.today}
              </Button>
            ) : (
              <span />
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                if (onClear) onClear();
                else onChange('');
                dismiss();
              }}
            >
              {labels.clear}
            </Button>
          </div>
        </div>
      </DatePopover>
    </div>
  );
}
